import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { cmsClient } from "@/lib/cmsClient";
import GET_COLOR from "../graphql/GetColor.graphql";

const colorResponseSchema = z.object({
    Color: z
        .object({
            id: z.string(),
            hexColor: z.string().nullable(),
        })
        .nullable(),
});

function readHexFromCache(colorId: string): string | null {
    try {
        const cached = cmsClient.cache.readQuery({
            query: GET_COLOR,
            variables: { colorId },
        });
        const parsed = colorResponseSchema.safeParse(cached);
        if (!parsed.success) return null;
        return parsed.data.Color?.hexColor ?? null;
    } catch {
        return null;
    }
}

/**
 * Resolves a list of CMS color ids to a `{ id: hexColor }` map.
 *
 * Reads synchronously from Apollo's cache first so a fully-cached set
 * resolves on the initial render, then async-fetches only the missing
 * ids. The unique id list is stable across renders for the same id-set
 * so the effect doesn't re-fire when callers rebuild the input array.
 */
export function useColorHexMap(ids: (string | null | undefined)[]): {
    hexById: Record<string, string>;
} {
    // Stable string fingerprint of the id-set; primitives compare cheaply
    // and let useMemo / useEffect short-circuit when the set is unchanged.
    const idsKey = Array.from(new Set(ids.filter((id): id is string => !!id)))
        .sort()
        .join(",");
    const uniqueIds = useMemo(
        () => (idsKey ? idsKey.split(",") : []),
        [idsKey],
    );

    const [hexById, setHexById] = useState<Record<string, string>>(() => {
        const initial: Record<string, string> = {};
        for (const id of uniqueIds) {
            const hex = readHexFromCache(id);
            if (hex) initial[id] = hex;
        }
        return initial;
    });

    useEffect(() => {
        if (uniqueIds.length === 0) {
            setHexById((prev) => (Object.keys(prev).length === 0 ? prev : {}));
            return;
        }

        const fromCache: Record<string, string> = {};
        const missing: string[] = [];
        for (const id of uniqueIds) {
            const hex = readHexFromCache(id);
            if (hex) {
                fromCache[id] = hex;
            } else {
                missing.push(id);
            }
        }

        setHexById((prev) => {
            const sameKeys =
                Object.keys(prev).length === Object.keys(fromCache).length &&
                Object.keys(prev).every((k) => prev[k] === fromCache[k]);
            return sameKeys ? prev : fromCache;
        });
        if (missing.length === 0) return;

        let cancelled = false;
        Promise.all(
            missing.map((colorId) =>
                cmsClient
                    .query<unknown>({
                        query: GET_COLOR,
                        variables: { colorId },
                    })
                    .then((res) => {
                        const parsed = colorResponseSchema.safeParse(res.data);
                        const hex = parsed.success
                            ? (parsed.data.Color?.hexColor ?? null)
                            : null;
                        return [colorId, hex] as const;
                    })
                    .catch(() => [colorId, null] as const),
            ),
        ).then((entries) => {
            if (cancelled) return;
            setHexById((prev) => {
                const next = { ...prev };
                for (const [id, hex] of entries) {
                    if (hex) next[id] = hex;
                }
                return next;
            });
        });
        return () => {
            cancelled = true;
        };
    }, [uniqueIds]);

    return { hexById };
}
